/**
 * The arrow beside ▶ Executar (docs/refactor/33-publicar.md): Executar, or Publicar — the project brought up and
 * shared at a public link to try away from home, free and with no account. From the web it goes straight to the
 * computer at home, like ▶. The result is the link to open (the front end when there is one), the other services
 * below it, and, on the computer that shares them, "Parar de compartilhar".
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation } from '@tanstack/react-query'
import { ChevronDown, Globe, Hourglass, Loader2, Play, Square, X } from 'lucide-react'
import { ApiError, api } from '../lib/api'
import { isDesktop, openExternal, tunnelClose } from '../lib/desktop'
import { useI18n } from '../lib/i18n'
import { runPrompt } from '../lib/prompts'
import { PUBLISHED_EVENT, publishedIn, refusalKey, UNPUBLISH_MARKER, type PublishOutcome } from '../lib/publish'
import { notRunReason, useRepositoryRun } from '../lib/repositoryRun'
import type { CatalogPrompt, RepositoryLink, WorkOrder } from '../lib/types'
import { Dropdown, DropdownItem } from './Dropdown'
import { PUBLISH_ACTION_EVENT } from '../lib/composer'

export function PublishMenu({ repository, prompt, onRun, busy }: {
  repository: RepositoryLink
  /** The catalog's "run.publish"; none (an old server) hides the item. */
  prompt: CatalogPrompt | undefined
  /** ▶ Executar, as the main button does. */
  onRun: () => void
  busy: boolean
}) {
  const { t, locale } = useI18n()
  const [outcome, setOutcome] = useState<PublishOutcome | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  const finished = useCallback(
    (order: WorkOrder) => {
      if (order.status !== 'Done') {
        setProblem(order.status === 'Stopped' ? t('run.stopped') : t('run.failedRun'))
        return
      }
      // The agent closes the order a moment before the app at home has opened the links and sent its full output
      // (PromptQueueService.KeepDesktopOutputAsync): the links are waited for, up to a minute, before giving up.
      void (async () => {
        let latest = order
        for (let attempt = 0; attempt < 12; attempt++) {
          const result = publishedIn(latest.lastReply ?? latest.lastReport ?? '')
          if (result.published.length > 0 || result.refused.length > 0) {
            if (result.published.length > 0) setOutcome(result)
            else setProblem(t(refusalKey(result.refused[0]!.reason)))
            return
          }
          await new Promise((resolve) => setTimeout(resolve, 5000))
          latest = (await api.workOrders.list().catch(() => [] as WorkOrder[])).find((o) => o.id === order.id) ?? latest
        }
        const reason = notRunReason(latest)
        setProblem(reason ? t('run.notRun', { reason }) : t('publish.nothing'))
      })()
    },
    [t],
  )
  const { order, phase, follow } = useRepositoryRun(repository.id, finished, 'publish')

  // /publicar in the prompt box: the same as choosing Publicar here (lib/composer.ts).
  useEffect(() => {
    const onPublish = () => {
      if (phase === 'idle' && prompt && !publish.isPending) publish.mutate()
    }
    window.addEventListener(PUBLISH_ACTION_EVENT, onPublish)
    return () => window.removeEventListener(PUBLISH_ACTION_EVENT, onPublish)
  })

  // On the computer that shares, the links show the moment the tunnels open.
  useEffect(() => {
    const onPublished = (event: Event) => {
      const detail = (event as CustomEvent<PublishOutcome>).detail
      if (detail.published.length) setOutcome({ ...detail, published: [...detail.published].sort((a, b) => Number(b.main) - Number(a.main)) })
    }
    window.addEventListener(PUBLISHED_EVENT, onPublished)
    return () => window.removeEventListener(PUBLISHED_EVENT, onPublished)
  }, [])

  const publish = useMutation({
    mutationFn: () => runPrompt(prompt!, repository.id, locale),
    onSuccess: (started) => {
      setOutcome(null)
      setProblem(null)
      follow(started.id)
    },
    onError: (error) => setProblem(error instanceof ApiError && error.code === 'remote_off' ? t('run.remoteOff') : t('run.failed')),
  })
  const stopPublishing = useMutation({
    mutationFn: async () => {
      if (order && phase !== 'idle') await api.workOrders.stop(order.id)
      // Here, the app closes its links; from the web, the computer at home is asked to (lib/publish.ts).
      if (isDesktop()) await tunnelClose()
      else await api.promptQueue.send(UNPUBLISH_MARKER, repository.id)
    },
    onSuccess: () => setOutcome(null),
  })

  // The card hangs below the arrow, outside the header (which clips what overflows it).
  const anchor = useRef<HTMLSpanElement>(null)
  const [at, setAt] = useState<{ top: number; left: number } | null>(null)
  useEffect(() => {
    if (!outcome) return
    const place = () => {
      const box = anchor.current?.getBoundingClientRect()
      if (box) setAt({ top: box.bottom + 8, left: Math.max(16, Math.min(box.left - 200, window.innerWidth - 576)) })
    }
    place()
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [outcome])

  const main = outcome?.published.find((s) => s.main) ?? outcome?.published[0]
  const others = outcome?.published.filter((s) => s !== main) ?? []

  return (
    <>
      <span ref={anchor} className="publish-anchor" aria-hidden />
      <Dropdown
        label={t('publish.menu')}
        buttonClassName="btn btn--sm btn--icon run-bar__more"
        button={phase === 'idle' ? <ChevronDown size={14} /> : phase === 'working' ? <Square size={11} fill="currentColor" /> : <Hourglass size={13} />}
      >
        {(close) => (
          <>
            <DropdownItem icon={<Play size={14} />} label={t('run.play')} hint={t('run.playHint')} disabled={busy} onSelect={() => { close(); onRun() }} />
            {prompt && (
              <DropdownItem
                icon={publish.isPending ? <Loader2 size={14} className="spin" /> : <Globe size={14} />}
                label={t('publish.action')}
                side={t('publish.side')}
                hint={t('publish.hint')}
                disabled={publish.isPending || phase !== 'idle'}
                onSelect={() => { close(); publish.mutate() }}
              />
            )}
            {(phase !== 'idle' || outcome) && (
              <DropdownItem icon={<Square size={13} />} label={t('publish.stop')} danger onSelect={() => { close(); stopPublishing.mutate() }} />
            )}
          </>
        )}
      </Dropdown>
      {phase !== 'idle' && !outcome && <span className="tiny run-bar__notice" role="status">{t('publish.working', { name: repository.name })}</span>}
      {problem && <span className="tiny run-bar__notice run-bar__notice--bad" role="status">{problem}</span>}
      {main && at && createPortal(
        <div className="publish-result" role="status" style={{ top: at.top, left: at.left }}>
          <button type="button" className="btn btn--ghost btn--icon btn--sm publish-result__close" aria-label={t('common.close')} onClick={() => setOutcome(null)}>
            <X size={13} />
          </button>
          <span className="tiny muted">{t('publish.openThis')}</span>
          <span className="publish-result__main">
            <a href={main.url} target="_blank" rel="noreferrer" onClick={(event) => { event.preventDefault(); void openExternal(main.url) }}>
              <strong>{main.url}</strong>
            </a>{' '}
            <span className="tiny muted">({main.name})</span>
          </span>
          {others.length > 0 && (
            <span className="tiny muted">
              {t('publish.alsoUp')}
              {others.map((s) => (
                <span key={s.url} className="publish-result__other">
                  {' '}{s.name}: <a href={s.url} target="_blank" rel="noreferrer" onClick={(event) => { event.preventDefault(); void openExternal(s.url) }}>{s.url.replace('https://', '')}</a>
                </span>
              ))}
            </span>
          )}
          <span className="tiny muted">{t(isDesktop() ? 'publish.untilDesktop' : 'publish.untilWeb')}</span>
        </div>,
        document.body,
      )}
    </>
  )
}
